export type SamplePrompt = {
  n: number;
  title: string;
  text: string;
  tips: string[];
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
  { name: 'DeepSeek', url: () => 'https://chat.deepseek.com/' },
];

export const demoOutput = {
  title: 'MOTION TO DISMISS — [Case Name]',
  sections: [
    { heading: 'Legal Standard', lines: ['Applicable rule, pleading standard and controlling authority to verify'] },
    { heading: 'Statement of Facts', lines: ['Neutral, chronological account drawn only from the complaint', 'No characterizations · only the facts as pleaded'] },
    { heading: 'Argument', lines: ['Each ground for dismissal, applied element by element'] },
  ],
};

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Motion to Dismiss',
    text: `Act as a Senior Litigator specialized in technical drafting of federal and state court pleadings. Your goal is to generate a professional, persuasive and procedurally sound 'Motion to Dismiss' that can be filed after attorney review.

Open with the caption, identifying the [Court and Jurisdiction], the [Case Name and Number], the [Moving Party] and the [Opposing Party]. The tone must be formal, measured and precise, avoiding rhetorical excess that could undermine credibility with the court.

Write a concise introduction stating the relief requested and the specific procedural basis for dismissal, such as [Grounds for Dismissal: e.g. Failure to State a Claim, Lack of Personal Jurisdiction, Improper Venue].

Draft the Legal Standard section, explaining the applicable rule [Governing Rule or Statute] and the pleading standard the court must apply. Mark any case citations as [CITATION TO VERIFY] rather than inventing authority.

Develop the Statement of Facts strictly from the allegations of the complaint, summarizing the [Key Allegations] in neutral, chronological order.

Build the Argument section with a numbered heading for each ground. For each one, identify the required elements, explain why the complaint fails to plead [Missing Element or Defect], and connect the defect to the relief requested.

Close with a Conclusion requesting dismissal [With or Without Prejudice], followed by the signature block for [Attorney Name, Bar Number, Firm].

If key information is missing to complete the bracketed fields, ask me the necessary questions before responding.

Important: do not invent citations, case numbers, rulings or references. If you cannot verify them with real sources, say so clearly.`,
    tips: ['Confirm the local rules for page limits, formatting and meet-and-confer requirements before filing.'],
  },
  {
    n: 2,
    title: 'Contract Risk Review',
    text: `Act as a Senior Transactional Attorney with extensive experience in commercial contracts and risk allocation. Your task is to perform a structured risk review of the agreement I provide and deliver a clear, prioritized memo for [Client Name].

Begin by identifying the [Agreement Type], the [Parties and Their Roles], the [Governing Law] and the [Term and Renewal Provisions]. Summarize the commercial purpose of the deal in two sentences.

Review the agreement clause by clause, focusing on indemnification, limitation of liability, termination rights, IP ownership, confidentiality, payment terms and dispute resolution. For each clause, state whether it is [Favorable, Neutral, or Unfavorable] to my client and explain why.

Create a 'Risk Register' as a numbered list. For each risk, include the clause reference, a severity rating [High / Medium / Low], the potential exposure, and a proposed redline in plain contract language.

Flag any missing provisions a prudent attorney would expect in an agreement of this type, given my client's position as [Buyer / Seller / Licensor / Licensee].

Conclude with a short negotiation strategy listing the top three points to push on and the concessions my client could reasonably offer.

If key information is missing to complete the bracketed fields, ask me the necessary questions before responding.`,
    tips: [
      'Remove or anonymize client-identifying information before pasting the contract into any AI tool.',
      'Cross-check every proposed redline against the governing law you specified.',
    ],
  },
  {
    n: 3,
    title: 'Demand Letter',
    text: `Act as a Persuasive Counselor and experienced civil litigator. Your task is to draft a firm, professional 'Demand Letter' on behalf of [Client Name] addressed to [Recipient Name and Address].

Open with the date, a clear subject line referencing [Matter Description], and a statement that the firm represents the client in this matter.

Set out the facts in a concise, chronological narrative: what happened on [Date of Incident], the [Nature of the Breach or Injury], and the documents that support the claim [Supporting Evidence].

Explain the legal basis for liability in plain English, referencing [Legal Theory: e.g. Breach of Contract, Negligence] without overstating the law. Mark any statutory or case references as [CITATION TO VERIFY].

Itemize the damages claimed, including [Amount of Damages] and how each amount was calculated.

State the demand clearly: the payment or action required, the deadline [Response Deadline], and the consequences of non-compliance, while keeping the tone professional and avoiding threats that could raise ethical concerns.

Close with the attorney's signature block [Attorney Name, Firm, Contact Details] and a note that the letter is sent for settlement purposes where applicable.

If key information is missing to complete the bracketed fields, ask me the necessary questions before responding.`,
    tips: ['Check your jurisdiction’s rules on settlement communications and threats of criminal prosecution before sending.'],
  },
];
